import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Country {
  id: number;
  name: string;
}

interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface UpdateStateProps {
  handleClose: () => void;
  stateidvalues: any;
  fetchStates: () => void;
  selectedVendorId: string; // Add this prop
}

const BoardUpdateState: React.FC<UpdateStateProps> = ({
  handleClose,
  stateidvalues,
  fetchStates,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    vendorId: selectedVendorId || "", // Initialize with passed vendor ID
    countryId: "",
    name: "",
    shortCode: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch (error) {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Fetch Countries (Vendor Based)
  ========================= */
  useEffect(() => {
    const fetchCountries = async () => {
      const vendorIdToUse = selectedVendorId || form.vendorId;
      if (!vendorIdToUse) {
        setCountries([]);
        return;
      }

      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/country/vendor/${vendorIdToUse}/findAll`
        );
        setCountries(res?.data || res || []);
      } catch (error) {
        showSnackbar("Failed to load countries", "error");
      }
    };

    fetchCountries();
  }, [APIURL, selectedVendorId, form.vendorId, showSnackbar]);

  /* =========================
     Prefill State Data
  ========================= */
  useEffect(() => {
    if (stateidvalues) {
      setForm({
        id: stateidvalues.id,
        vendorId: selectedVendorId || stateidvalues.vendorId || "", // Use selectedVendorId if available
        countryId: stateidvalues.countryId || "",
        name: stateidvalues.name || "",
        shortCode: stateidvalues.shortCode || "",
        isActive: stateidvalues.isActive
      });
    }
  }, [stateidvalues, selectedVendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.countryId || !form.name) {
      showSnackbar("Vendor, Country and State Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        vendorId: form.vendorId,
        countryId: Number(form.countryId),
        name: form.name,
        shortCode: form.shortCode || null,
        isActive: form.isActive
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/state`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "State updated successfully",
          "success"
        );
        fetchStates();
        handleClose();
      } else {
        showSnackbar(response?.message || "Failed to update state", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update state", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === form.vendorId);

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update State
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor-label">Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              label="Vendor"
              value={form.vendorId || ""}
              disabled
              inputProps={{ readOnly: true }}
            >
              <MenuItem value={form.vendorId || ""}>
                {selectedVendor?.vendorName || form.vendorId || "Select Vendor"}
              </MenuItem>
            </Select>
            <FormHelperText>Vendor is selected from the parent list</FormHelperText>
          </FormControl>
          <input type="hidden" name="vendorId" value={form.vendorId} />
          <input type="hidden" name="id" value={form.id} />
        </Grid>

        {/* Country Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="Country"
            name="countryId"
            value={form.countryId}
            onChange={handleChange}
          >
            <MenuItem value="" disabled>
              Select Country
            </MenuItem>
            {countries.map(country => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* State Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="State Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardUpdateState;